/**
 * Theme Toggle Module
 * Manages light/dark theme switching with localStorage persistence
 */

const THEME_KEY = 'burger-prime-theme';
const THEME_TOGGLE_ID = 'theme-toggle';

let currentTheme = 'light';

/**
 * Initialize theme toggle button and load saved preference
 */
export function initializeThemeToggle() {
    createThemeToggleButton();
    loadSavedTheme();
}

/**
 * Create floating theme toggle button
 */
function createThemeToggleButton() {
    const themeToggle = document.createElement('button');
    themeToggle.id = THEME_TOGGLE_ID;
    themeToggle.innerHTML = '🌙';
    themeToggle.setAttribute('aria-label', 'Toggle dark mode');

    themeToggle.style.cssText = `
        position: fixed;
        bottom: 30px;
        left: 30px;
        width: 50px;
        height: 50px;
        border-radius: 50%;
        border: none;
        background: var(--accent-gold);
        color: var(--neutral-black);
        font-size: 1.5rem;
        cursor: pointer;
        box-shadow: 0 4px 15px rgba(255, 215, 0, 0.4);
        transition: all 0.3s ease;
        z-index: 998;
        display: none; /* Hidden until dark mode is fully implemented */
    `;

    themeToggle.addEventListener('click', toggleTheme);
    document.body.appendChild(themeToggle);
}

/**
 * Toggle between light and dark themes
 */
function toggleTheme() {
    const newTheme = currentTheme === 'light' ? 'dark' : 'light';
    setTheme(newTheme);
}

/**
 * Set theme and persist to localStorage
 * @param {string} theme - Theme name ('light' or 'dark')
 */
export function setTheme(theme) {
    if (theme !== 'light' && theme !== 'dark') {
        console.warn(`Invalid theme: ${theme}. Using 'light'.`);
        theme = 'light';
    }

    currentTheme = theme;
    applyTheme(theme);
    localStorage.setItem(THEME_KEY, theme);
}

/**
 * Apply theme to the document
 * @param {string} theme - Theme name to apply
 */
function applyTheme(theme) {
    document.documentElement.setAttribute('data-theme', theme);

    const themeToggle = document.getElementById(THEME_TOGGLE_ID);
    if (themeToggle) {
        themeToggle.innerHTML = theme === 'light' ? '🌙' : '☀️';
        themeToggle.setAttribute('aria-label',
            theme === 'light' ? 'Switch to dark mode' : 'Switch to light mode'
        );
    }
}

/**
 * Load saved theme preference from localStorage
 */
function loadSavedTheme() {
    const savedTheme = localStorage.getItem(THEME_KEY);
    if (savedTheme) {
        setTheme(savedTheme);
    }
}

/**
 * Get current theme
 * @returns {string} Current theme name
 */
export function getCurrentTheme() {
    return currentTheme;
}

/**
 * Cleanup theme toggle (remove button and listeners)
 */
export function cleanupThemeToggle() {
    const themeToggle = document.getElementById(THEME_TOGGLE_ID);
    if (themeToggle) {
        themeToggle.remove();
    }
}
